import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  LuArrowLeft,
  LuArrowUpDown,
  LuBoxes,
  LuDatabase,
  LuFilter,
  LuHistory,
  LuWarehouse,
} from "react-icons/lu";

import MaterialStockEntryModal from "../../components/materialStocks/MaterialStockEntryModal";
import MaterialStockLedger from "../../components/materialStocks/MaterialStockLedger";
import MaterialStockTotals from "../../components/materialStocks/MaterialStockTotals";
import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import DetailHeader from "../../components/materialLines/DetailHeader";
import DetailSection from "../../components/materialLines/DetailSection";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  detailCardClass,
  detailSectionTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useConfirmPopup from "../../context/useConfirmPopup";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearMaterialStock,
  fetchMaterialStockLedger,
  getMaterialStockLedger,
  getMaterialStockLedgerLoaded,
  getMaterialStockLedgerLoading,
} from "../../features/materialStocks/materialStocksSlice";
import { DELETE } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { MaterialStockLedgerRowProps } from "../../types/materialStock";

type EntryRow = Extract<MaterialStockLedgerRowProps, { kind: "entry" }>;

interface FilterFormInput {
  fromDate: string;
  toDate: string;
}

const noDates: FilterFormInput = { fromDate: "", toDate: "" };

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ", href: route.materials },
  { label: "સ્ટોક વિગતો" },
];

// One material's stock: its bars in stock, then their history laid out like the party account page (a filter shown
// from its button, newest rows first, pages), with the entries made by hand at counting or settlement time. Opened from
// the materials list.
const MaterialStockDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const confirmDelete = useConfirmPopup();
  const { id } = useParams<{ id: string }>();
  const materialId = Number(id) || 0;
  // Each material's page keeps its own place; the materials tile starts them at page 1 again.
  const listKey = `materials:stock-${materialId}`;

  const ledger = useAppSelector(getMaterialStockLedger);
  const isLoading = useAppSelector(getMaterialStockLedgerLoading);
  const isLoaded = useAppSelector(getMaterialStockLedgerLoaded);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.materialStocksCreate
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialStocksUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.materialStocksDelete
  );
  const canOpenPurchase = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesRead
  );
  const canOpenCutting = hasPermission(
    loggingUser?.permissions,
    permission.cuttingOrdersRead
  );

  // The dates in use when the user last left the page (to open a purchase, say), else none.
  const savedDates: FilterFormInput = savedList.filters || noDates;

  const { handleSubmit, register, reset } = useForm<FilterFormInput>({
    defaultValues: savedDates,
  });

  // The dates the history was asked for: the filter's, once applied.
  const [applied, setApplied] = useState<FilterFormInput>(savedDates);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [modal, setModal] = useState<{ entry: EntryRow | null } | null>(null);

  // Coming back to the page (from a purchase, say) shows the same page and dates.
  useEffect(() => {
    dispatch(
      saveListState({
        key: listKey,
        value: { page, pageSize, showFilter, filters: applied },
      })
    );
  }, [dispatch, listKey, page, pageSize, showFilter, applied]);

  const loadLedger = useCallback(() => {
    if (materialId) {
      dispatch(
        fetchMaterialStockLedger({
          material_id: materialId,
          fromDate: applied.fromDate || null,
          toDate: applied.toDate || null,
        })
      );
    }
  }, [materialId, applied, dispatch]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  useEffect(
    () => () => {
      dispatch(clearMaterialStock());
    },
    [materialId, dispatch]
  );

  // The page's material (not one opened before); a date change keeps it and only reloads the history.
  const stock = ledger && ledger.material.id === materialId ? ledger : null;
  const isFirstLoad = materialId > 0 && (!isLoaded || (isLoading && !stock));

  // A user limited to recent records: the history starts later than asked, and the older rows only count in the stock.
  const clampedFrom =
    stock?.from_date && stock.from_date !== applied.fromDate
      ? stock.from_date
      : null;

  // Newest first, a page at a time; each row already carries the stock after it. The page is kept in range when
  // a delete leaves fewer rows.
  const rows = stock ? [...stock.rows].reverse() : [];
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Back to where the user came from, or to the materials list when the page was opened directly.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(route.materials);
    }
  };

  const handleOnFilterSubmit = (data: FilterFormInput) => {
    setApplied({ fromDate: data.fromDate, toDate: data.toDate });
    setPage(1);
  };

  const handleClearFilter = () => {
    reset(noDates);
    setApplied({ ...noDates });
    setPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleSaved = () => {
    setModal(null);
    loadLedger();
  };

  const handleDelete = async (entry: EntryRow) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (!confirmed) return;

    try {
      const response: any = await DELETE(`/v1/admin/material-stocks/${entry.id}`);

      if (response?.data?.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        loadLedger();
      } else {
        toast.error(response?.data?.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        { ...(toastStyles as any) }
      );
    }
  };

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>મટીરીયલ સ્ટોક વિગતો</h1>
      </div>

      {isFirstLoad ? (
        <div className={detailCardClass}>
          <LoadingState />
        </div>
      ) : !stock ? (
        <div className={cx(detailCardClass, "text-center text-gray-500")}>
          મટીરીયલ ની વિગતો મળી નથી
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          <div className={detailCardClass}>
            <DetailHeader icon={LuWarehouse} title={stock.material.name} id={stock.material.id}>
              <Button variant="outline" className="max-sm:flex-1" onClick={handleBack}>
                <LuArrowLeft className="h-4 w-4" />
                પાછા જાઓ
              </Button>
              {canCreate && stock.sizes.length > 0 && (
                <Button
                  variant="primary"
                  className="max-sm:flex-1"
                  onClick={() => setModal({ entry: null })}
                >
                  <LuArrowUpDown className="h-4 w-4" />
                  સ્ટોક વધ-ઘટ
                </Button>
              )}
            </DetailHeader>

            {stock.material.description && (
              <p className="mt-4 break-words text-sm text-gray-600">
                {stock.material.description}
              </p>
            )}

            <DetailSection title="કુલ સ્ટોક" icon={LuBoxes}>
              <MaterialStockTotals sizes={stock.sizes} />
              <p className="mt-3 text-xs text-gray-500">
                સ્ટોક = ખરીદી − કટીંગ ઓર્ડર માં મોકલેલ + ઉમેરેલ − બાદ (બાર માં)
              </p>
            </DetailSection>
          </div>

          <div className={actionBarClass}>
            <h3 className={detailSectionTitleClass}>
              <LuHistory className="h-5 w-5 shrink-0" />
              સ્ટોક નો ઇતિહાસ
            </h3>

            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setShowFilter(!showFilter)}
            >
              <LuFilter className="h-4 w-4" />
              {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
            </Button>
          </div>

          {showFilter && (
            <div className={cardClass}>
              <div className={cardHeaderClass}>
                <div className={cx(cardTitleClass, "text-lg")}>ફિલ્ટર</div>
              </div>

              <div className={cardContentClass}>
                <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                  <div className={filterGridClass}>
                    <div className="space-y-2">
                      <label className={filterLabelClass}>તારીખ થી</label>
                      <div className="relative">
                        <LuDatabase className={fieldIconClass} />
                        <Input
                          type="date"
                          className={fieldInputClass()}
                          {...register("fromDate")}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={filterLabelClass}>તારીખ સુધી</label>
                      <div className="relative">
                        <LuDatabase className={fieldIconClass} />
                        <Input
                          type="date"
                          className={fieldInputClass()}
                          {...register("toDate")}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={filterFooterClass}>
                    <Button
                      type="button"
                      variant="outline"
                      className="max-sm:flex-1"
                      onClick={handleClearFilter}
                    >
                      ફિલ્ટર સાફ કરો
                    </Button>

                    <Button
                      type="submit"
                      variant="primary"
                      className="max-sm:flex-1"
                    >
                      ફિલ્ટર લાગુ કરો
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {clampedFrom && (
            <p className="text-xs text-orange-700">
              {moment(clampedFrom).format("DD-MM-YYYY")} પહેલાં ની ખરીદી, કટીંગ ઓર્ડર અને એન્ટ્રી અહીં બતાવી નથી, પણ સ્ટોક માં ગણાઈ છે
            </p>
          )}

          <MaterialStockLedger
            rows={pageRows}
            pageNumber={currentPage}
            pageSize={pageSize}
            isLoading={isLoading}
            canOpenPurchase={canOpenPurchase}
            canOpenCutting={canOpenCutting}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onEdit={(entry) => setModal({ entry })}
            onDelete={handleDelete}
          />

          {!isLoading && rows.length > 0 && (
            <Pagination
              itemsPerPage={pageSize}
              totalItems={rows.length}
              paginate={setPage}
              currentPage={currentPage}
              onPageSizeChange={handlePageSizeChange}
              itemLabel="એન્ટ્રી"
            />
          )}
        </div>
      )}

      {modal && stock && (
        <MaterialStockEntryModal
          material={{
            id: stock.material.id,
            name: stock.material.name,
            sizes: stock.sizes,
          }}
          entry={modal.entry}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </AppLayout>
  );
};

export default MaterialStockDetailPage;
